package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.geom.SnapLine
import dev.lingvo.shared.screenshots.geom.SnapResult
import dev.lingvo.shared.screenshots.geom.SnapTargets

/** Frozen sorted targets are built once per gesture; binary search bounds the per-move work. */
internal class SnapCandidates private constructor(val targets: SnapTargets, private val geometry: EditorGeometry) {
    fun move(rect: Rect, delta: Point, zoom: Double, command: Boolean = false): SnapResult {
        require(zoom.isFinite() && zoom > 0)
        if (command) return SnapResult(delta, emptyList())
        val threshold = 6 / zoom
        val x = anchors(rect.x + delta.x, rect.w).flatMap { nearby(targets.x, it, threshold) }.distinct()
        val y = anchors(rect.y + delta.y, rect.h).flatMap { nearby(targets.y, it, threshold) }.distinct()
        return geometry.snapMove(rect, delta, targets.copy(x = x, y = y), threshold)
    }
    companion object {
        fun build(page: Page, board: Board, moving: Set<String>, safe: Rect? = null,
            geometry: EditorGeometry = EngineEditorGeometry): SnapCandidates {
            val backgrounds = allLayers(page.layers).filterIsInstance<dev.lingvo.shared.screenshots.doc.BackgroundLayer>().map { it.base.id }
            val targets = geometry.targets(page, board, moving + backgrounds, safe)
            val order = compareBy<SnapLine> { it.pos }.thenBy { it.kind.ordinal }.thenBy { it.sourceId.orEmpty() }
            // Equal spacing is phase 3; the editor deliberately supplies no neighbour gap targets.
            return SnapCandidates(targets.copy(x = targets.x.sortedWith(order), y = targets.y.sortedWith(order), neighbours = emptyList()), geometry)
        }
    }
}
private fun anchors(start: Double, size: Double): List<Double> = listOf(start, start + size / 2, start + size)
private fun nearby(lines: List<SnapLine>, anchor: Double, threshold: Double): List<SnapLine> {
    var low = 0; var high = lines.size
    while (low < high) { val mid = (low + high) / 2; if (lines[mid].pos < anchor - threshold) low = mid + 1 else high = mid }
    val found = mutableListOf<SnapLine>()
    while (low < lines.size && lines[low].pos <= anchor + threshold) found += lines[low++]
    return found
}
